import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelInterventionRequest,
  fetchFollowUpRequests,
  fetchMissionRequests,
  fetchInterventionCandidates,
  fetchMissionAssignments,
  fetchMissionSkills,
  remindInterventionRequest,
  saveMissionSkills,
  saveMissionTerms,
  saveTravelFee,
  sendInterventionRequest,
  voidTravelFee,
  type FollowUpFilters,
  type FollowUpPage,
  type MissionAssignmentDetail,
  type InterventionCandidatePage,
  type MissionAssignment,
  type MissionSkills,
  type MissionTermsInput,
  type SendRequestInput,
  type TravelFeeInput,
} from '../api/interventionRequestsApi';
import { samePopulation } from '../utils/samePopulation';

const KEY = 'interventionRequests';

/**
 * Every write moves both reads: a send changes the row's status and greys the technician out in
 * the drawer, and saving the skills changes the ranking. Invalidating the whole tree keeps the two
 * from disagreeing, rather than three surgical keys that would drift.
 */
const useAssignmentMutation = <TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  /**
   * Other query prefixes this particular write moves. Only the two that transition the ticket
   * name one: `['adminIssues']` is a Jira listing call, and refetching it after saving the
   * mission skills would be a round trip that cannot change a single cell.
   */
  alsoInvalidate: string[][] = []
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      alsoInvalidate.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
    },
  });
};

/**
 * The Jira listing, which is what the tab's rows and their intervention date come from.
 *
 * Named by the two writes that **move a ticket between statuses** (D17): a send transitions it to
 * « Intervention Programmée » and out of a tab that lists only « en cours de qualification », and
 * a release brings it back. Invalidating it is what makes the row leave — and it leaves *there*,
 * after the mutation has settled, never optimistically while the request is still in flight.
 */
const JIRA_LISTING = [['adminIssues']];

/**
 * What a page of missions is assigned to.
 *
 * `keepPreviousData` keeps the assignment columns filled while the next page loads: without it the
 * technician and status cells blank out on every page turn, which reads as "nobody is assigned".
 * A short `staleTime` because a pending request is something someone else may be answering right
 * now — this is the one read on the screen that goes stale on its own.
 */
export const useMissionAssignments = (serviceDeskId: number, issueKeys: string[]) =>
  useQuery<MissionAssignment[]>({
    queryKey: [KEY, 'assignments', serviceDeskId, issueKeys],
    queryFn: () => fetchMissionAssignments(serviceDeskId, issueKeys),
    enabled: issueKeys.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

/**
 * The ranked candidates for one mission. `enabled` is false until the drawer has both a mission
 * and a postal code: without a postal code there is no department, and asking for a ranking of
 * the whole country is precisely what the hard geographic filter exists to prevent.
 *
 * ⚠️ **Read fresh on every open, against the app's defaults.** `src/index.tsx` sets
 * `staleTime: 5 min` and `refetchOnMount: false`, so an empty ranking stayed cached for five
 * minutes and reopening the drawer did not re-read it. The candidates depend on data edited on a
 * **different screen** — a technician's departments and skills — so the ops team would widen a
 * perimeter, come back, and still be told « aucun technicien ne couvre ce département », which by
 * then is false. `refetchOnWindowFocus` does not save it: it only refetches a query that is
 * already stale. A drawer is opened to make a decision; it must not decide on a five-minute-old
 * answer.
 */
export const useInterventionCandidates = (
  issueKey: string | null,
  serviceDeskId: number,
  postalCode: string,
  /** The mission's day. In the key, so saving a date re-ranks instead of serving the old list. */
  interventionDate: string | null
) =>
  useQuery<InterventionCandidatePage>({
    queryKey: [KEY, 'candidates', issueKey, serviceDeskId, postalCode, interventionDate],
    queryFn: () =>
      fetchInterventionCandidates(issueKey as string, serviceDeskId, postalCode, interventionDate),
    enabled: issueKey !== null && postalCode.length >= 2,
    staleTime: 0,
    refetchOnMount: true,
  });

/**
 * The mission's own skills, read fresh when the drawer opens. `staleTime: 0` and a refetch on
 * mount: this is the value a write is built on, so reading it from cache is how the drawer came
 * to delete what it had not seen.
 */
export const useMissionSkills = (issueKey: string | null, serviceDeskId: number) =>
  useQuery<MissionSkills>({
    queryKey: [KEY, 'skills', issueKey, serviceDeskId],
    queryFn: () => fetchMissionSkills(issueKey as string, serviceDeskId),
    enabled: issueKey !== null,
    staleTime: 0,
    refetchOnMount: true,
  });

/** Both families in one write (D66b-f) — the drawer has a single Save, and so has the API. */
export const useSaveMissionSkills = () =>
  useAssignmentMutation(
    ({
      issueKey,
      serviceDeskId,
      ...skills
    }: {
      issueKey: string;
      serviceDeskId: number;
      specialtyIds: number[];
      expertiseIds: number[];
    }) => saveMissionSkills(issueKey, serviceDeskId, skills)
  );

export const useSendInterventionRequest = () =>
  useAssignmentMutation((input: SendRequestInput) => sendInterventionRequest(input), JIRA_LISTING);

export const useCancelInterventionRequest = () =>
  useAssignmentMutation(
    ({ issueKey, serviceDeskId, requestId }: { issueKey: string; serviceDeskId: number; requestId: number }) =>
      cancelInterventionRequest(issueKey, serviceDeskId, requestId),
    JIRA_LISTING
  );

/** E15 — same keys as a release: the row's reminder count and its 24 h lock move. */
export const useRemindInterventionRequest = () =>
  useAssignmentMutation(
    ({ issueKey, serviceDeskId, requestId }: { issueKey: string; serviceDeskId: number; requestId: number }) =>
      remindInterventionRequest(issueKey, serviceDeskId, requestId),
    JIRA_LISTING
  );

/** F15 — the whole terms card (R10). `JIRA_LISTING`: the deadline is a field of the Jira-backed listing. */
export const useSaveMissionTerms = () =>
  useAssignmentMutation(
    ({ issueKey, serviceDeskId, input }: { issueKey: string; serviceDeskId: number; input: MissionTermsInput }) =>
      saveMissionTerms(issueKey, serviceDeskId, input),
    JIRA_LISTING
  );

/** F19 — a technician's travel fee. The default `[KEY]` invalidation already covers `useMissionRequests`, which carries the costs. */
export const useSaveTravelFee = () =>
  useAssignmentMutation(
    ({ issueKey, serviceDeskId, fee }: { issueKey: string; serviceDeskId: number; fee: TravelFeeInput }) =>
      saveTravelFee(issueKey, serviceDeskId, fee)
  );

export const useVoidTravelFee = () =>
  useAssignmentMutation(
    ({ issueKey, serviceDeskId, operationId }: { issueKey: string; serviceDeskId: number; operationId: number }) =>
      voidTravelFee(issueKey, serviceDeskId, operationId)
  );

/**
 * One page of the follow-up table.
 *
 * `keepPreviousData` so the table does not blank out between two pages or between two filters —
 * an empty table under a spinner reads as "no results", which is the one thing it must never say
 * by accident (R-054). The whole filter object is the key: it is what the URL carries, so two
 * views can never share a cache entry.
 *
 * `staleTime: 15_000`, like the assignments: a `pending` row is something a technician may be
 * answering right now, and this screen exists to show that it has not been.
 */
export const useFollowUpRequests = (filters: FollowUpFilters) =>
  useQuery<FollowUpPage>({
    queryKey: [KEY, 'followUp', filters],
    queryFn: () => fetchFollowUpRequests(filters),
    /**
     * ⚠️ **Previous rows are kept for a page turn, and for nothing else.**
     *
     * `keepPreviousData` unconditionally would leave one desk's rows on screen under another
     * desk's name while the picker and the URL already show the new one — the cross-page mismatch
     * #415 fixed on the ticket screen, rebuilt here. A page turn is the one change where the rows
     * still belong to the view being shown, so it is the one that keeps them; every other change
     * falls back to the skeleton.
     */
    placeholderData: (previous: FollowUpPage | undefined, previousQuery) =>
      samePopulation('page', previousQuery?.queryKey?.[2] as FollowUpFilters | undefined, filters)
        ? previous
        : undefined,
    staleTime: 15_000,
    /**
     * ⚠️ Not asked before a desk is resolved. The desk list arrives one render later than the
     * page, so the first pass carries `serviceDeskId: 0` — and the schema requires a positive
     * integer, so every visit started with a **400 in the console** before this guard. Measured
     * in the browser, not deduced.
     */
    enabled: filters.serviceDeskId > 0,
  });

/**
 * The chronology of one mission, and the Jira access list beside it. `enabled` is what keeps the
 * drawer from fetching a history the moment the page mounts: it asks only once a row is open.
 */
export const useMissionRequests = (issueKey: string | null, serviceDeskId: number) =>
  useQuery<MissionAssignmentDetail>({
    queryKey: [KEY, 'mission', issueKey, serviceDeskId],
    queryFn: () => fetchMissionRequests(issueKey as string, serviceDeskId),
    enabled: !!issueKey,
    // Fresh on each opening: an acceptance made while the drawer was closed must show.
    staleTime: 0,
    refetchOnMount: true,
  });
